import {
  ReportFilters,
  FormatedReportType,
} from '@views/pages/Reporting/Reports/types.ts';
import { toNumber } from 'lodash';
import { useNavigate } from 'react-router-dom';
import {
  useCancelReportGeneratingMutation,
  useGeneratePatientReportMutation,
  useOwnProfileQuery,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useReportStore } from '@store';
import { type Id as ToastId, toast } from 'react-toastify';
import { FormattedToastMessage } from '@components';
import { t } from 'i18next';
import { Box } from '@mui/material';
import { useDownloadsNotification } from '@common/hooks';
import { useEffect, useState } from 'react';
import { ReportsFolderPreviewTableEntry } from '@views/pages/Reporting/ReportsFolderPreview/hooks';

const reportTypesObject: Record<string, FormatedReportType> = {
  patients_report: 'patients',
  tasks_report: 'tasks',
  records_report: 'record',
  users_report: 'users',
};

export type ColumnNames =
  | 'name'
  | 'description'
  | 'folder_name'
  | 'created_at'
  | 'item_count'
  | 'organization';

export interface ReportingTableEntry {
  id: string;
  name: string;
  description: string;
  favorite: boolean;
  private: boolean;
  created_by: {
    id: string;
    name: string;
  };
  folder: {
    id: string;
    name: string;
    organization: {
      id: string;
      name: string;
    } | null;
  };
  report_type:
    'patients_report' | 'tasks_report' | 'users_report' | 'records_report';
  created_at: string;
}

export type ReportingTableFilters = Pick<
  ReportFilters,
  'sort_by' | 'sort_order' | 'page' | 'limit'
>;

interface IUseReportingTableProps {
  filters: ReportingTableFilters;
  setFilters: (filters: ReportingTableFilters) => void;
}

let toastId: ToastId;
export const useReportingTable = ({
  filters,
  setFilters,
}: IUseReportingTableProps) => {
  const { sort_order, sort_by } = filters;
  const navigate = useNavigate();
  const { clearAll, setIsFormInitialized } = useReportStore();
  const { event } = useDownloadsNotification();

  const [historyReportId, setHistoryReportId] = useState('');
  const [isReportHistoryModalOpen, setIsReportHistoryModalOpen] =
    useState(false);

  const { data: userProfile } = useOwnProfileQuery();

  const { mutateAsync: reportMutateAsync, isPending } =
    useGeneratePatientReportMutation();

  const { mutateAsync: cancelAsync, isPending: isCanceling } =
    useCancelReportGeneratingMutation();

  const handleOpenHistoryModal = (id: string) => {
    setIsReportHistoryModalOpen(true);
    setHistoryReportId(id);
  };

  const handleCloseHistoryModal = () => {
    setIsReportHistoryModalOpen(false);
    setHistoryReportId('');
  };

  const handleChangeOrder = (colName: ColumnNames) => {
    setFilters({
      ...filters,
      sort_by: colName as ReportingTableFilters['sort_by'],
      sort_order: sort_order === 'ASC' ? 'DESC' : 'ASC',
    });
  };

  const onPageIndexChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setFilters({ ...filters, page: page + 1 });
  };

  const onPageSizeChange = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({ ...filters, limit: toNumber(event.target.value), page: 1 });
  };

  const handleEditReport = (report_id: string) => {
    clearAll();
    setIsFormInitialized(true);
    navigate(`/reporting/edit/${report_id}`);
  };

  const handleCancel = async (report_id: string) => {
    if (isCanceling) return;

    try {
      await cancelAsync(report_id);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleGenerateReportCSV = async (
    row: ReportsFolderPreviewTableEntry,
  ) => {
    toast.update(toastId, { autoClose: 1 });

    try {
      const { id, report_type } = row;
      const response = await reportMutateAsync({
        report_id: id,
        report_type: reportTypesObject[report_type],
        props: {},
      });

      toastId = toast.info(
        <FormattedToastMessage
          title={t(
            `reporting.report.preview.generatingReport.title.${response.status}`,
          )}
          message={t(
            `reporting.report.preview.generatingReport.message.${response.status}`,
          )}
        />,
        {
          closeButton:
            response.status === 201 ? (
              <Box
                onClick={() => handleCancel(response.data.id)}
                sx={{ width: '60px' }}
              >
                Cancel
              </Box>
            ) : (
              <></>
            ),
          autoClose: false,
        },
      );
    } catch (error) {
      formatApiError(error);
    }
  };

  const handleRowClick = (
    e: React.MouseEvent | React.KeyboardEvent,
    id: string,
  ) => {
    const url = `/reporting/items/${id}`;
    // support open-in-new-tab
    if (e.metaKey || e.ctrlKey) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(url);
    }
  };

  useEffect(() => {
    if (event?.type === 'report-download.completed') {
      toast.update(toastId, { autoClose: 1 });
    }
  }, [event]);

  useEffect(() => {
    return () => {
      toast.update(toastId, { autoClose: 1 });
    };
  }, []);

  return {
    handleChangeOrder,
    onPageIndexChange,
    onPageSizeChange,
    sort_order,
    sort_by,
    handleEditReport,
    handleGenerateReportCSV,
    isDownloading: isPending,
    user_id: userProfile?.id,
    handleRowClick,
    isReportHistoryModalOpen,
    handleOpenHistoryModal,
    handleCloseHistoryModal,
    historyReportId,
  };
};
